import PropTypes from 'prop-types';
import React from 'react';
import { translate } from '../../../common/i18n';
import Dialog from './Dialog';

const Save = React.memo(({ onChange }) => {
    const [strategy, setStrategy] = React.useState('none');
    const [showOnlyBest, setShowOnlyBest] = React.useState(true);

    const changeStrategy = str => {
        setStrategy(str);
        onChange(str);
    };

    const toggleBestBots = () => {
        setShowOnlyBest(!showOnlyBest);
    };

    // Função para calcular o crescimento diário com base na época
    const calculateGrowth = (baseCount) => {
        // Reference date: Jan 1, 2023 (day 0)
        const referenceDate = new Date(2024, 0, 1).getTime();
        const currentDate = new Date();
        const currentTime = currentDate.getTime();

        // Calculate days since reference date (including day 0)
        const daysSinceReference = Math.floor((currentTime - referenceDate) / (1000 * 60 * 60 * 24));

        // Parse the base count
        const baseCountNum = parseFloat(baseCount.replace('.', ''));

        // Create an array to count plays for each day since reference date
        let totalPlays = 0;

        // For each day since the reference date (including day 0)
        for (let day = 0; day <= daysSinceReference; day++) {
            // Calculate the day of month for this historical day
            const historicalDate = new Date(referenceDate + day * 24 * 60 * 60 * 1000);
            const historicalDayOfMonth = historicalDate.getDate(); // 1-31

            // Calculate plays for this day: day 1 of month = 30 plays, day 31 of month = 0 plays
            const playsForThisDay = Math.max(0, 30 - (historicalDayOfMonth - 1));

            // Add to total
            totalPlays += playsForThisDay;
        }

        // Add the accumulated plays to the base count
        const newCount = baseCountNum + totalPlays;

        // Format with thousands separator
        return newCount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    // Função para calcular variações na taxa de vitória com base na época
    const calculateWinRate = (baseWinRate) => {
        // Get today's date at 00:00:00 (start of day)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayEpoch = today.getTime();

        // Get the day of month (1-31)
        const dayOfMonth = today.getDate();

        // Parse the base win rate
        const baseRateNum = parseFloat(baseWinRate.replace(',', '.').replace('%', ''));

        // Generate a deterministic variation based on today's epoch and the base rate
        // Using both ensures different bots get different variations on the same day
        const seed = todayEpoch + baseRateNum * 100;

        // Create a pseudo-random value from the seed that's consistent for the day
        // Use a simple hash function to generate a value between 0-1
        const hashValue = Math.sin(seed) * 10000;
        const normalizedHash = Math.abs(hashValue % 100) / 100; // 0-1 range

        // Scale variation based on day of month: higher variation early in month
        const dayFactor = (31 - dayOfMonth) / 30;

        // Calculate variation within range of -1.5% to +1.5%
        const variation = (normalizedHash * 3 - 1.5) * dayFactor;

        // Apply variation to base rate
        let newRate = baseRateNum + variation;

        // Ensure rate stays within reasonable bounds
        newRate = Math.max(90, Math.min(99.5, newRate));

        // Format back to string with comma as decimal separator
        return `${newRate.toFixed(1).replace('.', ',')  }%`;
    };

    // Dados base dos melhores bots
    const botBaseData = [
        {
            id       : 'over_under_low_risk',
            name     : 'Low Risk',
            stars    : 5,
            baseCount: '954',
            winRate  : '97,6%',
        },
        {
            id       : 'over_under_sharpshooter',
            name     : 'Sharpshooter',
            stars    : 4,
            baseCount: '748',
            winRate  : '94,2%',
        },
        {
            id       : 'rise_fall_side_changer',
            name     : 'Side Changer',
            stars    : 5,
            baseCount: '1245',
            winRate  : '96,8%',
        },
        {
            id       : 'even_odd_even_odd_statistics',
            name     : 'Even Odd Statistics',
            stars    : 4,
            baseCount: '1451',
            winRate  : '93,5%',
        },
        {
            id       : 'touch_no_touch_supertrend_30s',
            name     : 'Supertrend 30s',
            stars    : 5,
            baseCount: '514',
            winRate  : '95,7%',
        },
        {
            id       : 'rise_fall_3_bbs_extreme',
            name     : '3 BBs Extremes',
            stars    : 4,
            baseCount: '742',
            winRate  : '92,1%',
        },
        {
            id       : 'rise_fall_flagship',
            name     : 'Flagship',
            stars    : 5,
            baseCount: '665',
            winRate  : '98,2%',
        },
        {
            id       : 'rise_fall_same_direction_ticks',
            name     : 'Same Direction Ticks',
            stars    : 4,
            baseCount: '388',
            winRate  : '94,8%',
        },
        {
            id       : 'over_under_27',
            name     : 'Over Under 27',
            stars    : 4,
            baseCount: '995',
            winRate  : '93,9%',
        },
        {
            id       : 'even_odd_alternating',
            name     : 'Alternating',
            stars    : 4,
            baseCount: '746',
            winRate  : '91,7%',
        },
    ];

    // Calcula o número atualizado de usos e taxa de vitória para cada bot
    const bestBots = botBaseData.map(bot => ({
        ...bot,
        usageCount: calculateGrowth(bot.baseCount),
        winRate   : calculateWinRate(bot.winRate),
    }));

    // Array com dados dos novos bots
    const newBots = [
        {
            id  : 'rise_fall_scalping_pullback',
            name: 'Scalping Pullback',
        },
        {
            id  : 'even_odd_even_division',
            name: 'Even Division',
        },
        {
            id  : 'rise_fall_ticks_in_reverse',
            name: 'Ticks In Reverse',
        },
        {
            id  : 'reset_call_put_ticks_in_reverse_reset',
            name: 'Ticks In Reverse Reset',
        },
        {
            id  : 'custom_differs_in_between',
            name: 'Differs In Between',
        },
        {
            id  : 'over_under_low_risk',
            name: 'Low Risk',
        },
    ];


    return (
        <div className="main-strategy-list">
            <div style={{ padding: '10px', display: 'flex', justifyContent: 'start' }}>
                <button
                    onClick={toggleBestBots}
                    style={{
                        padding     : '8px 16px',
                        background  : showOnlyBest ? '#4CAF50' : '#F44336',
                        color       : 'white',
                        border      : 'none',
                        borderRadius: '4px',
                        cursor      : 'pointer',
                        fontSize    : '14px',
                        fontWeight  : 'bold',
                    }}
                >
                    {showOnlyBest ? 'Show All Bots' : 'Show Best Bots Only'}
                </button>
            </div>
            <ul className="main-list" style={{ overflowY: 'scroll', height: 'calc(100vh - 160px)' }}>
                <li className="category best" style={{ display: showOnlyBest ? 'block' : 'none' }}>
                    <h3 style={{ margin: '10px 0', color: 'white', fontWeight: 'bold' }}>The best bots</h3>
                    <ul style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'flex-start', padding: '10px 0' }}>
                        {bestBots.map((bot) => (
                            <li key={bot.id} style={{ width: 'calc(25% - 12px)', minWidth: '180px' }}>
                                <div
                                    style={{
                                        background    : 'transparent',
                                        color         : 'white',
                                        padding       : '15px',
                                        borderRadius  : '8px',
                                        border        : '1px solid #F44336',
                                        boxShadow     : '0 4px 8px rgba(0,0,0,0.2)',
                                        height        : '100%',
                                        display       : 'flex',
                                        flexDirection : 'column',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div>
                                        <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 'bold', color: 'white' }}>{bot.name}</h4>
                                        <div style={{ display: 'flex', marginBottom: '8px' }}>
                                            {'★★★★★'.split('').map((star, i) => (
                                                <span key={i} style={{ color: i < bot.stars ? '#FFD700' : '#ccc' }}>{star}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                                        <span style={{ fontSize: '12px', color: 'white' }}>Used {bot.usageCount} times</span>
                                        <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'white' }}>{bot.winRate} Win</span>
                                    </div>
                                    <button
                                        onClick={() => changeStrategy(bot.id)}
                                        style={{
                                            background  : '#F44336',
                                            color       : 'white',
                                            border      : 'none',
                                            borderRadius: '4px',
                                            padding     : '8px 0',
                                            marginTop   : '15px',
                                            cursor      : 'pointer',
                                            fontWeight  : 'bold',
                                            width       : '100%',
                                        }}
                                    >
                                        Load Bot
                                    </button>
                                </div>
                            </li>
                        ))}


                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    New Bots
                    <ul>
                        {newBots.map((bot) => (
                            <li key={bot.id}>
                                <button
                                    style={{ background: '#2196F3', color: 'white' }}
                                    onClick={() => changeStrategy(bot.id)}
                                >
                                    {bot.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Accumulators
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('accumulator_accumulator_breaker')}>
                                Accumulator Breaker
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('accumulator_fast_profit')}>Fast Profit</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('accumulator_in_the_accumulator_zone')}>
                                In The Accumulator Zone
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('accumulator_keltner_bb')}>Keltner BB</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('accumulator_price_percentage')}>
                                Price Percentage
                            </button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Custom
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('custom_digit_over_differs')}>
                                Digit Over Differs
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('custom_adjusting_barriers')}>
                                Adjusting Barriers
                            </button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Even / Odd
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_4_digits_even')}>4 Even Digits</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_5ers')}>5ers Even Odd</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_alternating')}>Alternating</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_double_screen_digits')}>
                                Double Screen Digits
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_even_odd_statistics')}>
                                Even Odd Statistics
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_extremes')}>Even Odd Extremes</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_ladder_even_odd')}>Ladder Even Odd</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_percentage')}>EO Percentage</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('even_odd_even_division')}>Even Division</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Higher / Lower
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_6p_higher_lower')}>
                                6P Higher Lower
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_auto_barrier')}>Auto Barrier</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_fast_barrier_changer')}>
                                Fast Barrier Changer
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_first_5')}>First 5</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_hilo_changer')}>Hilo Changer</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_hma_higher_recover')}>
                                HMA Higher Recover
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_stake_increasing')}>
                                Higher Stake Increasing
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_switcher')}>Switcher</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('higher_lower_ultima_pro')}>Ultima Pro</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Matches / Differs
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_5_ticks')}>Matches 5 Ticks</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_double_digit')}>
                                Double Digits
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_frequent_digits')}>
                                Frequent 50 Digits
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_most_frequent_list')}>
                                Most Frequent List
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_sequential_differs')}>
                                Sequential Differs
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_turbo_differ')}>
                                Turbo Differs
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_zig_zag')}>Zig-Zag Digits</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('matches_differs_differs_prob')}>Differs Prob</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Multipliers
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('multipliers_bb_trend')}>Multiplier BB Trend</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('multipliers_hull_multiplier')}>
                                Hull Multiplier
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('multipliers_multi_tf_stoch_rsi')}>
                                Multi TF Stoch RSI
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('multipliers_zig_zag')}>Multiplier Zig-zag</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('multipliers_multiplier_ema_adx')}>
                                Multiplier EMA ADX
                            </button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Over / Under
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('over_under_27')}>Over Under 27</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_69')}>Over Under 69</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_all_digits')}>
                                Under Over All Digits
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_cascade')}>Cascade</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_compound')}>Over Compound</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_multiple_prediction')}>
                                Multiple Predictions
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_no_martingale')}>No Martingale</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_percent_recover')}>
                                Under Over Percent Recover
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_pred_changer')}>
                                Prediction Changer
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_sequence')}>Over Sequence</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_sharpshooter')}>Sharpshooter</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('over_under_low_risk')}>Low Risk</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Reset Call / Put
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('reset_call_put_ticks_in_reverse_reset')}>Ticks In Reverse Reset</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Rise / Fall
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_3_bbs_extreme')}>3 BBs Extremes</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_4_12_cross')}>SMA Cross</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_4_mas')}>4 Moving Averages</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_7_ticks_reversal')}>
                                7 Ticks Reversal
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_above_below')}>Above and Below</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_aroon_ticks')}>Over Compound</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_atr_change')}>ATR Change</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_awesome_sma')}>Awesome SMA</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_bb_slow')}>BB Slow</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_candle_colors')}>Candle Colors</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_candle_pattern')}>Candle Patterns</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_candle_sma_color')}>
                                Candle SMA Color
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_candle_trend_continuation')}>
                                Candle Trend Continuation
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_chop_ema_stochrsi')}>
                                Chop, EMA, Stoch RSI
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_chop_rsi')}>Chop RSI</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_detrended')}>Detrended</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ema_1_3')}>EMA 1/3 1min</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ema_cci_cross')}>CCI EMA Cross</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ema_cross')}>EMA Cross</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ema_macd_stoch_atr')}>
                                EMA, MACD, Stoch, ATR
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ema_ribbon')}>EMA Ribbon</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_emas_major_ticks')}>
                                EMAs Major Ticks
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_end_of_candle')}>End of Candle</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_fast_macd')}>Fast MACD</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_flagship')}>Flagship</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_high_tower')}>High Tower</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_highs_lows')}>Highs and Lows</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_histo_ticks')}>Histo Ticks</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_ichimoku')}>Ichimoku</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_keltener_sma')}>Keltener SMA</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_macd_rsi_ticks')}>MACD RSI Ticks</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_past_patterns')}>Past Patterns</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_pullback_2_layers')}>
                                Pullback 2 Layers
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_reversal_candles_end')}>
                                Reversal Candle's End
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_rsi_cci_engulfing')}>
                                RSI, CCI & Engulfing
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_rsi_sma_5min')}>RSI SMA 5min</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_same_direction_sma')}>
                                Same Direction SMA
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_scalping_pullback')}>
                                Scalping Pullback
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_same_direction_ticks')}>
                                Same Direction Ticks
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_sequence_candle_colors')}>
                                Sequence Candle Colors
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_sma_balance_of_power')}>
                                SMA Balance of Power
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_sma_macd_kc')}>SMA, MACD, and KC</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_stochastic_crosser')}>
                                Stochastic Crosser
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_triple_hma')}>Triple HMA</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_triple_macd')}>Triple MACD</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_triple_rsi')}>Triple RSI</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_triple_triangular')}>
                                Triple Triangular
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('rise_fall_triple_trix')}>Triple Trix</button>
                        </li>
                    </ul>
                </li>
                <li className="category" style={{ display: !showOnlyBest ? 'block' : 'none' }}>
                    Touch / No Touch
                    <ul>
                        <li>
                            <button onClick={() => changeStrategy('touch_no_touch_2_15min')}>Touch 2/15Min</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('touch_no_touch_cci_touch')}>CCI Touch</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('touch_no_touch_double_macd')}>Double MACD</button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('touch_no_touch_supertrend_30s')}>
                                Supertrend 30s
                            </button>
                        </li>
                        <li>
                            <button onClick={() => changeStrategy('touch_no_touch_trend_aroon_ma')}>
                                Trend Aroon MA
                            </button>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    );
});

Save.propTypes = {
    onChange: PropTypes.func.isRequired,
};

export default class StoreDialog extends Dialog {
    constructor() {
        const closeDialog = () => {
            this.close();
        };

        const onChange = arg => {
            this.limitsPromise(arg);
            closeDialog();
        };
        super('store-dialog', translate('Bot Store'), <Save closeDialog={closeDialog} onChange={onChange} />, {
            height   : 'auto',
            width    : '78em',
            resizable: false,
        });
        this.registerCloseOnOtherDialog();
    }

    change() {
        this.open();
        return new Promise(resolve => {
            this.limitsPromise = resolve;
        });
    }
}



// WEBPACK FOOTER //
// ./src/botPage/view/Dialogs/Store.js